import { useState, useCallback } from 'react';

import { UploadedAssetAttachment } from '@scale/shared/studio/types';
import { AttachmentType } from '@scale/shared/defaults';

import { unleashFeatureFlagEnabled } from '@scale/frontend-lib/featureFlag';

const useUnprocessedAttachments = () => {
  const [newAttachments, setNewAttachments] = useState<UploadedAssetAttachment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchUnprocessedAttachments = useCallback(
    async (attachments: UploadedAssetAttachment[]) => {
      if (!attachments.length) {
        return;
      }
      setLoading(true);

      const controller = new AbortController();

      // 5 second timeout:
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      // currently this is by user. we should make this by project in the future
      const assumeImage = await unleashFeatureFlagEnabled({
        feature: 'studio-privacy-mode-assume-image',
      });

      const resps = await Promise.all(
        attachments.map((at: any) => {
          const resp = fetch(at.content, { method: 'HEAD', signal: controller.signal }).catch(
            _e => {
              return undefined;
            },
          );
          return resp;
        }),
      );

      clearTimeout(timeoutId);

      const getHeaders = (headers: any) => {
        const headerObj: Record<string, string> = {};
        const keys = headers.keys();
        let header = keys.next();
        while (header.value) {
          headerObj[header.value] = headers.get(header.value);
          header = keys.next();
        }
        return headerObj;
      };

      const fetchedAttachments = attachments.map((a, i) => {
        let attachmentType = AttachmentType.Unprocessed;

        // if this flag is true, assume it is an image. should move to do this by project in the future
        if (assumeImage) {
          attachmentType = AttachmentType.Image;
          return { ...a, type: attachmentType };
        }

        const resp = resps[i];
        if (!resp) {
          return { ...a };
        }
        const contentType = getHeaders(resp.headers)['content-type'];

        if (contentType.match('image/*')) {
          attachmentType = AttachmentType.Image;
        } else if (contentType.match('text/*')) {
          attachmentType = AttachmentType.Text;
        } else if (contentType.match('video/*')) {
          attachmentType = AttachmentType.Video;
        } else if (contentType === 'application/pdf') {
          attachmentType = AttachmentType.PDF;
        } else if (contentType.match('audio/*')) {
          attachmentType = AttachmentType.Audio;
        }
        return { ...a, type: attachmentType };
      });

      setNewAttachments(fetchedAttachments);

      setLoading(false);
      return fetchedAttachments;
    },
    [],
  );

  return {
    attachments: newAttachments,
    isLoading: loading,
    fetchUnprocessedAttachments,
  } as const;
};

export default useUnprocessedAttachments;
